import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLElement} [summary] - The burger toggle.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 * @property {HTMLTemplateElement} [navigationTemplate] - The inert link tree, until it is rendered.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners(this);

    // Start the stamp on pointerdown so the first open's click handler doesn't carry it.
    this.refs.summary?.addEventListener('pointerdown', this.renderNavigation, { once: true, passive: true });

    if (this.refs.navigationTemplate?.hasAttribute('data-deferred')) this.#prefetchNavigation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.summary?.removeEventListener('pointerdown', this.renderNavigation);
  }

  /** @type {Promise<void> | undefined} */
  #navigationLoad;

  /**
   * The page render ships the link tree empty (data-deferred). Fill it at idle, but only where the drawer
   * is showing: desktop uses the mega menu and never needs it, and an early open still loads it.
   */
  #prefetchNavigation() {
    requestIdleCallback(() => {
      const container = this.closest('.header__drawer') ?? this;
      if (container.getClientRects().length === 0) return;

      this.#loadNavigation();
    });
  }

  /**
   * Fills the deferred template with the link tree. Shared by the prefetch and an early open; a failed
   * load can be retried by the next open.
   * @returns {Promise<void>}
   */
  #loadNavigation() {
    this.#navigationLoad ??= this.#fetchNavigation().catch((error) => {
      this.#navigationLoad = undefined;
      console.error(error);
    });

    return this.#navigationLoad;
  }

  /**
   * The link tree is the same on every page apart from which link is current, so it's rendered once from
   * the home page and kept for the session; markCurrentPage() then sets the current link for this page.
   * The storage key carries the menu version, so a menu edit or a country/language switch refetches it.
   */
  async #fetchNavigation() {
    const template = this.refs.navigationTemplate;
    const section = this.closest('.shopify-section');
    if (!template?.hasAttribute('data-deferred') || !section) return;

    const storageKey = `${NAVIGATION_STORAGE_PREFIX}${section.id}:${template.dataset.navigationVersion}`;
    let markup = readSessionStorage(storageKey);

    if (!markup) {
      // Not from the cache: on the home page it holds the page's own markup, where the template is empty
      const html = await sectionRenderer.getSectionHTML(section.id, false, new URL(Theme.routes.root_url, location.origin));
      markup = this.#findNavigationTemplate(html, section)?.innerHTML;
      if (!markup) throw new Error('Header drawer navigation missing from the section render');

      writeSessionStorage(storageKey, markup);
    }

    template.innerHTML = markup;
    markCurrentPage(template.content);
    template.removeAttribute('data-deferred');
  }

  /**
   * @param {string} html - The section's rendered HTML
   * @param {Element} section - This drawer's section, to match the drawer by position
   * @returns {HTMLTemplateElement | null} The matching drawer's populated template
   */
  #findNavigationTemplate(html, section) {
    const index = Array.from(section.querySelectorAll('header-drawer')).indexOf(this);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const template = doc.querySelectorAll('header-drawer template[ref="navigationTemplate"]')[index];

    if (!(template instanceof HTMLTemplateElement) || template.hasAttribute('data-deferred')) return null;
    return template.content.childElementCount ? template : null;
  }

  /**
   * Stamps the link tree out of its template on first press/open. Idempotent, so every trigger can call it.
   */
  renderNavigation = () => {
    const template = this.refs.navigationTemplate;
    if (!template?.isConnected) return;

    // Opened before the deferred link tree arrived: stamp it once it does
    if (template.hasAttribute('data-deferred')) {
      this.#loadNavigation().then(() => {
        if (!template.hasAttribute('data-deferred')) this.renderNavigation();
      });
      return;
    }

    this.refs.summary?.removeEventListener('pointerdown', this.renderNavigation);

    const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    this.#setupAnimatedElementListeners(content);
    template.replaceWith(content);
    delete this.refs.navigationTemplate;
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    this.renderNavigation();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners(/** @type {ParentNode} */ root) {
    const allAnimated = root.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

const NAVIGATION_STORAGE_PREFIX = 'header-drawer-navigation:';

/**
 * @param {string} key
 * @returns {string | null}
 */
function readSessionStorage(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Stores the link tree, dropping copies of older menu versions first. Storage that's full or blocked
 * just means the next page fetches it again.
 * @param {string} key
 * @param {string} value
 */
function writeSessionStorage(key, value) {
  try {
    for (const storedKey of Object.keys(sessionStorage)) {
      if (storedKey.startsWith(NAVIGATION_STORAGE_PREFIX) && storedKey !== key) sessionStorage.removeItem(storedKey);
    }
    sessionStorage.setItem(key, value);
  } catch {}
}

/**
 * Sets the current page's link the way the Liquid render does (link.current / link.child_active), since
 * the stored link tree was rendered for the home page.
 * @param {DocumentFragment} fragment
 */
function markCurrentPage(fragment) {
  for (const element of fragment.querySelectorAll('.menu-drawer__menu-item--active')) {
    element.classList.remove('menu-drawer__menu-item--active');
  }
  for (const element of fragment.querySelectorAll('[aria-current]')) element.removeAttribute('aria-current');

  const currentPath = normalizePath(location.pathname);

  for (const link of fragment.querySelectorAll('a[href]')) {
    const href = link.getAttribute('href');
    if (!href || normalizePath(new URL(href, location.href).pathname) !== currentPath) continue;

    link.setAttribute('aria-current', 'page');
    if (link.matches('.menu-drawer__menu-item--child, .menu-drawer__menu-item--parent')) {
      link.classList.add('menu-drawer__menu-item--active');
    }

    // Only the top-level summary shows child_active
    link
      .closest('.menu-drawer__menu-container')
      ?.querySelector(':scope > summary')
      ?.classList.add('menu-drawer__menu-item--active');
  }
}

/**
 * @param {string} path
 * @returns {string}
 */
function normalizePath(path) {
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
